import { apiSlice } from '../../store/api/apiSlice';
import { ENV_CONFIG } from '../../shared/config/env.config';

/** Angular's `DataService` was untyped; responses stay open until screens land. */
export type CounsellingResponse = any;
/** Request bodies Angular passed straight through as `records` payloads. */
export type CounsellingPayload = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

type Value = string | number | undefined | null;

/** Keeps the query-string key present even when the caller has no value. */
const q = (value: Value): string | number => value ?? '';

const apiGet = (url: string, params?: Record<string, unknown>) => ({
  url,
  params,
  service: 'api' as const,
});

const apiPost = (url: string, body?: CounsellingPayload) => ({
  url,
  method: 'POST' as const,
  body,
  service: 'api' as const,
});

/** Angular: dataService.getJSON('../assets/json/<file>') */
const staticJson = (file: string) => ({
  url: `assets/json/${file}`,
  service: 'static' as const,
});

// ── request shapes for the multi-argument calls ───────────────────────────────

export interface TeacherListRequest {
  /** wire: CnsId (Angular: records.counType) */
  counsellingId: Value;
  /** wire: PanelType */
  panelType: Value;
  /** wire: Sub */
  subject?: Value;
  /** wire: Loc */
  location?: Value;
  /** wire: BlkId */
  blockId?: Value;
  /** wire: MngId */
  managementId?: Value;
}

export interface VacancyListRequest {
  counsellingId: Value;
  gender?: Value;
  subject?: Value;
  panelId?: Value;
  blockId?: Value;
  managementId?: Value;
  /** wire: minType */
  minority?: Value;
}

export interface SelectVacancyRequest {
  counsellingId: Value;
  teacherId: Value;
  panelId?: Value;
  subject?: Value;
  blockId?: Value;
  managementId?: Value;
}

export interface RcNumRequest {
  /** wire: Typ */
  type: Value;
  /** wire: TchrTyp */
  teacherType: Value;
  /** wire: TrTyp */
  transferType: Value;
}

export interface VacancyNeedListRequest {
  udiseCode: Value;
  /** wire: Des */
  designation?: Value;
  subject?: Value;
  panel?: Value;
  /** wire: Flag */
  flag?: Value;
}

export interface RcRequest {
  counsellingId: Value;
  rcFlag?: Value;
  subject?: Value;
}

export interface PdfOrderCopyRequest {
  counsellingId: Value;
  rcFlag?: Value;
  type?: Value;
  /** wire: TeacherId */
  teacherId?: Value;
  /** wire: TchSts */
  teacherStatus?: Value;
  /** wire: Sub */
  subject?: Value;
  /** wire: counselling_type */
  counsellingType?: Value;
}

export interface SeniorityChallengeRequest {
  teacherId: Value;
  counsellingId: Value;
  /** wire: SenVacId */
  seniorityVacancyId: Value;
}

export interface TeacherVacancyRequest {
  /** wire: SchlId */
  schoolId: Value;
  panel?: Value;
  /** wire: Desgn */
  designation?: Value;
  subject?: Value;
  aided?: Value;
}

export interface SchoolListRequest {
  districtId: Value;
  counsellingId: Value;
  /** wire: flag - Angular branched on 2 but sent the same URL either way. */
  activeFlag: Value;
}

export interface SignedUrlRequest {
  /** wire: bcktId */
  bucketName: string;
  ext: string;
  /** wire: filename */
  fileName: string;
  expiry: number;
}

export interface UploadedFilesRequest {
  bucketName: string;
  fileName: string;
  expiry: number;
}

export const counsellingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── School lookup ────────────────────────────────────────────────────────

    /** Angular: GetSclName(udise) */
    getSclName: builder.query<CounsellingResponse, { udiseCode: Value }>({
      query: ({ udiseCode }) =>
        apiGet('GetSclName', { UdiseCode: q(udiseCode) }),
    }),

    // ── Static dropdown JSON (public/assets/json) ─────────────────────────────

    /** Angular: getCategory() */
    getCategory: builder.query<CounsellingResponse, void>({
      query: () => staticJson('Category.json'),
    }),
    /** Angular: getManagement() */
    getManagement: builder.query<CounsellingResponse, void>({
      query: () => staticJson('Management.json'),
    }),
    /** Angular: getPanel() */
    getPanel: builder.query<CounsellingResponse, void>({
      query: () => staticJson('Panel.json'),
    }),
    /** Angular: getStaff() */
    getStaff: builder.query<CounsellingResponse, void>({
      query: () => staticJson('StaffType.json'),
    }),
    /** Angular: getSubject() */
    getSubject: builder.query<CounsellingResponse, void>({
      query: () => staticJson('Subject.json'),
    }),
    /** Angular: getSchCategory() */
    getSchCategory: builder.query<CounsellingResponse, void>({
      query: () => staticJson('SchCategory.json'),
    }),
    /** Angular: getAllDistrict() - the JSON list, not /api/alldistrictlist */
    getAllDistrict: builder.query<CounsellingResponse, void>({
      query: () => staticJson('district.json'),
    }),
    getMinorityType: builder.query<CounsellingResponse, void>({
      query: () => staticJson('MinorityType.json'),
    }),

    // ── File API (absolute URLs, as in Angular) ──────────────────────────────

    /** Angular: getSignedUrl(bucketName, ext, fileName, expiry) */
    getSignedUrl: builder.query<CounsellingResponse, SignedUrlRequest>({
      query: ({ bucketName, ext, fileName, expiry }) => ({
        url: ENV_CONFIG.getSignedUrlApi,
        params: { bcktId: bucketName, ext, filename: fileName, expiry },
        // AWS API Gateway endpoint — drop the app's Authorization/Token headers.
        skipAuthHeaders: true,
      }),
    }),

    /** Angular: getUploadedFiles(bucketName, fileName, expiry) */
    getUploadedFiles: builder.query<CounsellingResponse, UploadedFilesRequest>({
      query: ({ bucketName, fileName, expiry }) => ({
        url: ENV_CONFIG.readingFileApi,
        params: { bcktId: bucketName, filename: fileName, expiry },
        // AWS API Gateway endpoint — drop the app's Authorization/Token headers
        // (Angular's interceptor stripped them; sending them fails CORS/auth).
        skipAuthHeaders: true,
      }),
    }),

    /**
     * Angular: uploadFile(path, file) - PUT straight at the pre-signed URL.
     * The auth headers are dropped because a signed URL carries its own
     * credentials and S3 rejects a second auth mechanism.
     */
    uploadFile: builder.mutation<
      CounsellingResponse,
      { signedUrl: string; file: File | Blob }
    >({
      query: ({ signedUrl, file }) => ({
        url: signedUrl,
        method: 'PUT',
        body: file,
        skipAuthHeaders: true,
      }),
    }),

    // ── Plan new counselling ─────────────────────────────────────────────────

    /** Angular: postPlanCounselling(data) */
    postPlanCounselling: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CnslMainPage', body),
    }),

    /** Angular: GetNewCounselling() */
    getNewCounselling: builder.query<CounsellingResponse, void>({
      query: () => apiGet('CnslMainPageGetAll'),
    }),

    /** Angular: EditCounselling(CnsId) */
    editCounselling: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('CnslMainPageGet', { CnsId: q(counsellingId) }),
    }),

    /** Angular: getRules() */
    getRules: builder.query<CounsellingResponse, void>({
      query: () => apiGet('CnslRulesGet'),
    }),

    /** Angular: getCounsellingList(counselling_type) */
    getCounsellingList: builder.query<
      CounsellingResponse,
      { counsellingType: Value }
    >({
      query: ({ counsellingType }) =>
        apiGet('getCounsellingList', {
          counselling_type: q(counsellingType),
        }),
    }),

    /** Angular: getCounsellingTypeData() */
    getCounsellingTypeData: builder.query<CounsellingResponse, void>({
      query: () => apiGet('getCounsellingType'),
    }),

    // ── Teacher lists ────────────────────────────────────────────────────────

    /** Angular: getTeacherList(records) */
    getTeacherList: builder.query<CounsellingResponse, TeacherListRequest>({
      query: (records) => apiGet('CnslTchListGet', teacherListParams(records)),
    }),

    /** Angular: getTeacherList1(records) */
    getTeacherList1: builder.query<CounsellingResponse, TeacherListRequest>({
      query: (records) => apiGet('CnslTchListGet1', teacherListParams(records)),
    }),

    /** Angular: getTeacherList2(records) */
    getTeacherList2: builder.query<CounsellingResponse, TeacherListRequest>({
      query: (records) => apiGet('CnslTchListGet2', teacherListParams(records)),
    }),

    /** Angular: getAidedSchTchList(cnsId) */
    getAidedSchTchList: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('CnslTchListGetopn', { cnsnId: q(counsellingId) }),
    }),

    /** Angular: getTeacherListForMpg(cnsnId) */
    getTeacherListForMpg: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('getTchlistForMpng', { cnsnId: q(counsellingId) }),
    }),

    // ── Transfer ─────────────────────────────────────────────────────────────

    /** Angular: getCounTransfer(records) */
    postCounTransfer: builder.mutation<CounsellingResponse, CounsellingPayload>(
      {
        query: (body) => apiPost('CnslTrnsferCUD', body),
      }
    ),

    /** Angular: getCounTransferLog(records) */
    postCounTransferLog: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CnslTransferLog', body),
    }),

    /** Angular: staffCounsellingPost(records) */
    staffCounsellingPost: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('ResetTchrDtlsUpdate', body),
    }),

    /** Angular: tranferTeacherPost(data) */
    tranferTeacherPost: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('StaffSchlIdUpd', body),
    }),

    /** Angular: transferVacAddSub(records) */
    transferVacAddSub: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('TransUpdtTchrVacList', body),
    }),

    /** Angular: transferVacAddSub2(records) */
    transferVacAddSub2: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('TransUpdtTchrVacList2', body),
    }),

    // ── Vacancy selection ────────────────────────────────────────────────────

    /** Angular: getVacancy(cnsId, gender, subject, panId, blkid, MngId, minority) */
    getVacancy: builder.query<CounsellingResponse, VacancyListRequest>({
      query: ({
        counsellingId,
        gender,
        subject,
        panelId,
        blockId,
        managementId,
        minority,
      }) =>
        apiGet('CnslTrnsferGetVacList', {
          CnsId: q(counsellingId),
          Gender: q(gender),
          Sub: q(subject),
          PanId: q(panelId),
          BlkId: q(blockId),
          MngId: q(managementId),
          minType: q(minority),
        }),
    }),

    /** Angular: getSelectVacancy(data) */
    getSelectVacancy: builder.query<CounsellingResponse, SelectVacancyRequest>({
      query: ({
        counsellingId,
        teacherId,
        panelId,
        subject,
        blockId,
        managementId,
      }) =>
        apiGet('CnslTrnsferGetVacPriList', {
          CnsId: q(counsellingId),
          TchrId: q(teacherId),
          // Angular sent this one lower-cased.
          panId: q(panelId),
          Sub: q(subject),
          BlkId: q(blockId),
          MngId: q(managementId),
        }),
    }),

    // ── Districts, education districts and blocks ────────────────────────────

    /** Angular: getState() / getBlock() / distlist() - all the same route. */
    getAllDistrictList: builder.query<CounsellingResponse, void>({
      query: () => apiGet('alldistrictlist'),
    }),

    /** Angular: getEduDist(EduDis) */
    getEduDist: builder.query<CounsellingResponse, { districtId: Value }>({
      query: ({ districtId }) => apiGet('GetEduDist', { Dist: q(districtId) }),
    }),

    /** Angular: getAllEduDist() */
    getAllEduDist: builder.query<CounsellingResponse, void>({
      query: () => apiGet('GetAllEduDist'),
    }),

    /** Angular: getAllBlock() */
    getAllBlock: builder.query<CounsellingResponse, void>({
      query: () => apiGet('getAllBlock'),
    }),

    /** Angular: getDisBlock(dis) */
    getDisBlock: builder.query<CounsellingResponse, { districtId: Value }>({
      query: ({ districtId }) =>
        apiGet('getAllBlock', { DisId: q(districtId) }),
    }),

    /** Angular: getEduDisBlock(EduDis) */
    getEduDisBlock: builder.query<
      CounsellingResponse,
      { eduDistrictId: Value }
    >({
      query: ({ eduDistrictId }) =>
        apiGet('getAllBlock', { EduDistId: q(eduDistrictId) }),
    }),

    /** Angular: getAllBlocks() - note the different route to getAllBlock(). */
    getAllBlocks: builder.query<CounsellingResponse, void>({
      query: () => apiGet('GetAllBlocks'),
    }),

    // ── Priority ─────────────────────────────────────────────────────────────

    /** Angular: GetPriorityMaster() */
    getPriorityMaster: builder.query<CounsellingResponse, void>({
      query: () => apiGet('GetPriorityMaster'),
    }),

    /** Angular: postNewPriority(records) */
    postNewPriority: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('PriorityMasterAdd', body),
    }),

    /** Angular: postCnsnPriority(records) */
    postCnsnPriority: builder.mutation<CounsellingResponse, CounsellingPayload>(
      {
        query: (body) => apiPost('PriorityListCUD', body),
      }
    ),

    /** Angular: getCnsnPriority(cnsn) */
    getCnsnPriority: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('GetPriorityList', { CnslId: q(counsellingId) }),
    }),

    // ── Counselling update status ────────────────────────────────────────────

    /** Angular: getcounsellingstatus(DistId) */
    getCounsellingUpdateStatus: builder.query<
      CounsellingResponse,
      { districtId: Value }
    >({
      query: ({ districtId }) =>
        apiGet('CounsellingUptSts', { DistId: q(districtId) }),
    }),

    /** Angular: postcouncellingupdate(data) */
    postCounsellingUpdate: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CounsellingUpdate', body),
    }),

    /** Angular: Allpostcouncellingupdate(data) */
    postAllCounsellingUpdate: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('AllCounsellingUpdate', body),
    }),

    /** Angular: Livegetcounsellingstatus(CnsId) */
    getLiveCounsellingStatus: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('LiveCounsellingUpdate', { CnsId: q(counsellingId) }),
    }),

    // ── Counselling status ───────────────────────────────────────────────────

    /** Angular: getCounsellingStatus() */
    getCounsellingStatus: builder.query<CounsellingResponse, void>({
      query: () => apiGet('CounsellingStatus'),
    }),

    /** Angular: getCounsellingWiseStatus(cnsn) */
    getCounsellingWiseStatus: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('CounsellingStatus', { cnsn: q(counsellingId) }),
    }),

    /** Angular: getOneCnsnSubSumry(cnsn) */
    getOneCnsnSubSumry: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('CounsellingStatusSub', { CnsId: q(counsellingId) }),
    }),

    /** Angular: getInterDistrict(distId) */
    getInterDistrict: builder.query<CounsellingResponse, { districtId: Value }>(
      {
        query: ({ districtId }) =>
          apiGet('CnsStsCheckGet', { DistId: q(districtId) }),
      }
    ),

    // ── Order copy and RC numbers ────────────────────────────────────────────

    /** Angular: GetTchrTyp() */
    getTchrTyp: builder.query<CounsellingResponse, void>({
      query: () => apiGet('GetTchrTyp'),
    }),

    /** Angular: GetRcNum(type, teachId, typeId) */
    getRcNum: builder.query<CounsellingResponse, RcNumRequest>({
      query: ({ type, teacherType, transferType }) =>
        apiGet('GetRcNum', {
          Typ: q(type),
          TchrTyp: q(teacherType),
          TrTyp: q(transferType),
        }),
    }),

    /** Angular: GetOfcrDetails() */
    getOfcrDetails: builder.query<CounsellingResponse, void>({
      query: () => apiGet('GetOfcrDetails'),
    }),

    /** Angular: UpdateOfcrDetails(data) */
    updateOfcrDetails: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('UpdateOfcrDetails', body),
    }),

    /** Angular: UpdateRcNum(data) */
    updateRcNum: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('UpdateRcNum', body),
    }),

    /** Angular: PostOrderTempalte(data) - typo kept only in the comment. */
    postOrderTemplate: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CnslOrderCopyCUD', body),
    }),

    /** Angular: GetOrderTemplate(cnsid, type) */
    getOrderTemplate: builder.query<
      CounsellingResponse,
      { counsellingId: Value; type: Value }
    >({
      query: ({ counsellingId, type }) =>
        apiGet('CnslOrderCopyGet', {
          CnslId: q(counsellingId),
          Type: q(type),
        }),
    }),

    /** Angular: getOrderCopyGridMaster() */
    getOrderCopyGridMaster: builder.query<CounsellingResponse, void>({
      query: () => apiGet('TemplateClmnMaster'),
    }),

    /** Angular: getRc(CnslId, RcFlag, Subject) */
    getRc: builder.query<CounsellingResponse, RcRequest>({
      query: ({ counsellingId, rcFlag, subject }) =>
        apiGet('CnslRcNoGet', {
          CnslId: q(counsellingId),
          RcFlag: q(rcFlag),
          Subject: q(subject),
        }),
    }),

    /** Angular: postSingleRc(data) */
    postSingleRc: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('CnslRcNoCUD', body),
    }),

    /** Angular: getPdfOrderCopy(data) */
    getPdfOrderCopy: builder.query<CounsellingResponse, PdfOrderCopyRequest>({
      query: ({
        counsellingId,
        rcFlag,
        type,
        teacherId,
        teacherStatus,
        subject,
        counsellingType,
      }) =>
        apiGet('CnslPdfDwnGet', {
          CnslId: q(counsellingId),
          RcFlag: q(rcFlag),
          Type: q(type),
          TeacherId: q(teacherId),
          TchSts: q(teacherStatus),
          Sub: q(subject),
          counselling_type: q(counsellingType),
        }),
    }),

    // ── Admin panel checks ───────────────────────────────────────────────────

    /** Angular: getCheckVacancy(udise) */
    getCheckVacancy: builder.query<CounsellingResponse, { udiseCode: Value }>({
      query: ({ udiseCode }) =>
        apiGet('CnslChckVacList', { Udise: q(udiseCode) }),
    }),

    /** Angular: getCheckCandidate(tchId) */
    getCheckCandidate: builder.query<CounsellingResponse, { teacherId: Value }>(
      {
        query: ({ teacherId }) =>
          apiGet('CnslChckTchList', { TchId: q(teacherId) }),
      }
    ),

    // ── Vacancy and need ─────────────────────────────────────────────────────

    /** Angular: getVacancyNeedList(udiseCode, des, sub, panel, flg) */
    getVacancyNeedList: builder.query<
      CounsellingResponse,
      VacancyNeedListRequest
    >({
      query: ({ udiseCode, designation, subject, panel, flag }) =>
        apiGet('GetVacancyNeed', {
          UdiseCode: q(udiseCode),
          Des: q(designation),
          Sub: q(subject),
          Panel: q(panel),
          Flag: q(flag),
        }),
    }),

    /** Angular: postVacancyNeed(data) */
    postVacancyNeed: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('CnsnUpdVacancyNeed', body),
    }),

    /** Angular: addVacancyNeed(data) */
    addVacancyNeed: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('CnslVacCUD', body),
    }),

    /** Angular: addVacancyNeedLog(data) */
    addVacancyNeedLog: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CnslVacLog', body),
    }),

    /** Angular: getDesgVacNeed(des, udise) */
    getDesgVacNeed: builder.query<
      CounsellingResponse,
      { designation: Value; udiseCode: Value }
    >({
      query: ({ designation, udiseCode }) =>
        apiGet('getDesVacNeed', {
          Desig: q(designation),
          udise: q(udiseCode),
        }),
    }),

    /** Angular: getTechVac(SchlId, panel, des, sub, aided) */
    getTechVac: builder.query<CounsellingResponse, TeacherVacancyRequest>({
      query: ({ schoolId, panel, designation, subject, aided }) =>
        apiGet('GetCnslVaccancy', {
          SchlId: q(schoolId),
          Panel: q(panel),
          Desgn: q(designation),
          Sub: q(subject),
          Aided: q(aided),
        }),
    }),

    /** Angular: techTransferVacPost(data) */
    techTransferVacPost: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CnslVacNewCUD', body),
    }),

    /** Angular: techTransferVacPostAided(data) */
    techTransferVacPostAided: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CnslVacNewCUDAided', body),
    }),

    /** Angular: GetVacncyUpdateSts(desig) */
    getVacncyUpdateSts: builder.query<
      CounsellingResponse,
      { designation: Value }
    >({
      query: ({ designation }) =>
        apiGet('TchrDesVacSts', { TchrType: q(designation) }),
    }),

    /** Angular: postVacancyUpdate(data) */
    postVacancyUpdate: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('TchrDesVacStsCUD', body),
    }),

    /** Angular: bulkVacancy() */
    getBulkVacancy: builder.query<CounsellingResponse, void>({
      query: () => apiGet('VacBulkReprt'),
    }),

    /** Angular: getBulkSchFromUdise(data) - a POST that reads. */
    getBulkSchFromUdise: builder.query<CounsellingResponse, CounsellingPayload>(
      {
        query: (body) => apiPost('getBulkSchId', body),
      }
    ),

    /** Angular: getResultantVacancySchools(data?) - a POST that reads. */
    getResultantVacancySchools: builder.query<
      CounsellingResponse,
      CounsellingPayload | void
    >({
      query: (body) => apiPost('getResultantVacancySchools', body),
    }),

    /** Angular: UpdateResultantVacancy(data) */
    updateResultantVacancy: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('UpdateResultantVacancy', body),
    }),

    // ── School details ───────────────────────────────────────────────────────

    /** Angular: getSchDetails(udise) */
    getSchDetails: builder.query<CounsellingResponse, { udiseCode: Value }>({
      query: ({ udiseCode }) =>
        apiGet('TchrPanelSchlGet', { Udise: q(udiseCode) }),
    }),

    /** Angular: postChangeSch(data) */
    postChangeSch: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('TchrPanelSchlCUD', body),
    }),

    /** Angular: getTrackChangeData(udise) */
    getTrackChangeData: builder.query<
      CounsellingResponse,
      { udiseCode: Value }
    >({
      query: ({ udiseCode }) =>
        apiGet('SchlDetlSate', { UdiseCode: q(udiseCode) }),
    }),

    /** Angular: getSchoolList(district, counsellingId, activeFlag) */
    getSchoolList: builder.query<CounsellingResponse, SchoolListRequest>({
      query: ({ districtId, counsellingId, activeFlag }) =>
        apiGet('getSchoolList', {
          district_id: q(districtId),
          counselling_id: q(counsellingId),
          flag: q(activeFlag),
        }),
    }),

    // ── Staff transfer and promotion ─────────────────────────────────────────

    /** Angular: GetStaffTran(EmisId) */
    getStaffTran: builder.query<CounsellingResponse, { emisId: Value }>({
      query: ({ emisId }) => apiGet('GetStaffTranDet', { EmisId: q(emisId) }),
    }),

    /** Angular: postPromotion(data) and PostcherTransProm(data) - same route. */
    postTeacherTransProm: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('TeacherTransPromCUD', body),
    }),

    /** Angular: updateStaffDetails(data) */
    updateStaffDetails: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('stfupdateall', body),
    }),

    /** Angular: GetTeacherTransAll(EmisId, counsId) */
    getTeacherTransAll: builder.query<
      CounsellingResponse,
      { emisId: Value; counsellingId: Value }
    >({
      query: ({ emisId, counsellingId }) =>
        apiGet('GetTeacherTransAppDet', {
          EmisId: q(emisId),
          cns_id: q(counsellingId),
        }),
    }),

    // ── BEO / DEO / CEO approval ─────────────────────────────────────────────

    /** Angular: getdropdownsteachingstaff(refresh) - refresh is RTKQ's job now. */
    getDropdownStaff: builder.query<CounsellingResponse, void>({
      query: () => apiGet('GetDropdown_Staff'),
    }),

    /** Angular: GetHmAprveSatus(cnsnId) */
    getHmAprveSatus: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('GetTeacherTransAppDet2', { CnsId: q(counsellingId) }),
    }),

    /** Angular: GetHmAprveSatusTechId(cnsnId, techId) */
    getHmAprveSatusTechId: builder.query<
      CounsellingResponse,
      { counsellingId: Value; teacherId: Value }
    >({
      query: ({ counsellingId, teacherId }) =>
        apiGet('GetTeacherTransAppDet2', {
          CnsId: q(counsellingId),
          TechId: q(teacherId),
        }),
    }),

    /** Angular: GetHmAprveSatusMutual(cnsnId, cnsnType) */
    getHmAprveSatusMutual: builder.query<
      CounsellingResponse,
      { counsellingId: Value; counsellingType: Value }
    >({
      query: ({ counsellingId, counsellingType }) =>
        apiGet('GetTeacherTransAppDet2', {
          CnsId: q(counsellingId),
          cnsnType: q(counsellingType),
        }),
    }),

    /** Angular: GetTchrPnlDistSts(loc, userId) */
    getTchrPnlDistSts: builder.query<
      CounsellingResponse,
      { location: Value; userId: Value }
    >({
      query: ({ location, userId }) =>
        apiGet('GetTchrPnlDistSts', {
          loc: q(location),
          user_id: q(userId),
        }),
    }),

    // ── Seniority ────────────────────────────────────────────────────────────

    /** Angular: getOneSeniorityChallenge(teachId, csnId, vacId) */
    getOneSeniorityChallenge: builder.query<
      CounsellingResponse,
      SeniorityChallengeRequest
    >({
      query: ({ teacherId, counsellingId, seniorityVacancyId }) =>
        apiGet('ChlCnsSenVacGet', {
          TchrId: q(teacherId),
          CnslId: q(counsellingId),
          SenVacId: q(seniorityVacancyId),
        }),
    }),

    /** Angular: getSeniorityChallenge(csnId) */
    getSeniorityChallenge: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('GetTeacherTransChlAprv', { CnsId: q(counsellingId) }),
    }),

    /** Angular: postVacancyChallenge(data) */
    postVacancyChallenge: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('ChlCnsSenVacCUD', body),
    }),

    /** Angular: getSeniorityOrder(data) - a POST that reads. */
    getSeniorityOrder: builder.query<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('CnslTchListGetSen', body),
    }),

    /** Angular: bulkUploadSeniority(data) */
    bulkUploadSeniority: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('CnslSenUpdtCUD', body),
    }),

    // ── Challenge vacancies ──────────────────────────────────────────────────

    /** Angular: getChallengeVac(csnId) */
    getChallengeVac: builder.query<
      CounsellingResponse,
      { counsellingId: Value }
    >({
      query: ({ counsellingId }) =>
        apiGet('GetVacChlng', { CnsId: q(counsellingId) }),
    }),

    /** Angular: postChallenge(data) */
    postChallenge: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('PostVacChlng', body),
    }),

    /** Angular: getChallengeDetails(indId) */
    getChallengeDetails: builder.query<CounsellingResponse, { id: Value }>({
      query: ({ id }) => apiGet('GetChlngAprGet', { Id: q(id) }),
    }),

    // ── Dashboard ────────────────────────────────────────────────────────────

    /** Angular: getEduDistDashboard(cnsId, directorate) */
    getEduDistDashboard: builder.query<
      CounsellingResponse,
      { counsellingId: Value; directorate: Value }
    >({
      query: ({ counsellingId, directorate }) =>
        apiGet('ChlCuntDist', {
          CnslId: q(counsellingId),
          direc: q(directorate),
        }),
    }),

    /** Angular: getOverAll(userId, flag) */
    getOverAll: builder.query<
      CounsellingResponse,
      { userId: Value; flag: Value }
    >({
      query: ({ userId, flag }) =>
        apiGet('CnsSummaryApp', { UserId: q(userId), Flag: q(flag) }),
    }),

    // ── Aided school ─────────────────────────────────────────────────────────

    /** Angular: vcntGoPost(data) */
    vcntGoPost: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('CnslGoCUD', body),
    }),

    /** Angular: getVcntGo(vacId) */
    getVcntGo: builder.query<CounsellingResponse, { vacancyId: Value }>({
      query: ({ vacancyId }) => apiGet('GetGoNo', { VacId: q(vacancyId) }),
    }),

    /** Angular: vcntApprovalPost(data) */
    vcntApprovalPost: builder.mutation<CounsellingResponse, CounsellingPayload>(
      {
        query: (body) => apiPost('aidedSchVcntApproval', body),
      }
    ),

    // ── Counselling mapping ──────────────────────────────────────────────────

    /** Angular: postCnsnMapping(data) */
    postCnsnMapping: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('postCnsnMapping', body),
    }),

    /** Angular: getTknCnsn(data) - removes a candidate despite the name. */
    postTknCnsnRemoveCandidate: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('tknCnsnRmveCndt', body),
    }),

    // ── Counselling start authentication and SMS ─────────────────────────────

    /** Angular: CslnAuth(data) */
    cslnAuth: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('StrtCnsnAuthGet', body),
    }),

    /** Angular: smsSending(data) */
    smsSending: builder.mutation<CounsellingResponse, CounsellingPayload>({
      query: (body) => apiPost('SendingSmsParGet1', body),
    }),

    /** Angular: PostSMSSendingOTPHistory(data) */
    postSmsSendingOtpHistory: builder.mutation<
      CounsellingResponse,
      CounsellingPayload
    >({
      query: (body) => apiPost('SendingSmsOtpHistory', body),
    }),
  }),
});

/** Shared by CnslTchListGet, CnslTchListGet1 and CnslTchListGet2. */
function teacherListParams({
  counsellingId,
  panelType,
  subject,
  location,
  blockId,
  managementId,
}: TeacherListRequest) {
  return {
    CnsId: q(counsellingId),
    PanelType: q(panelType),
    Sub: q(subject),
    Loc: q(location),
    BlkId: q(blockId),
    MngId: q(managementId),
  };
}

export const {
  // school lookup
  useGetSclNameQuery,
  // static dropdown JSON
  useGetCategoryQuery,
  useGetManagementQuery,
  useGetPanelQuery,
  useGetStaffQuery,
  useGetSubjectQuery,
  useLazyGetSubjectQuery,
  useGetSchCategoryQuery,
  useGetAllDistrictQuery,
  useGetMinorityTypeQuery,
  useGetSignedUrlQuery,
  useLazyGetSignedUrlQuery,
  useGetUploadedFilesQuery,
  useLazyGetUploadedFilesQuery,
  useUploadFileMutation,
  // plan new counselling
  usePostPlanCounsellingMutation,
  useGetNewCounsellingQuery,
  useLazyGetNewCounsellingQuery,
  useEditCounsellingQuery,
  useLazyEditCounsellingQuery,
  useGetRulesQuery,
  useGetCounsellingListQuery,
  useGetCounsellingTypeDataQuery,
  // teacher lists
  useGetTeacherListQuery,
  useLazyGetTeacherListQuery,
  useGetTeacherList1Query,
  useLazyGetTeacherList1Query,
  useGetTeacherList2Query,
  useLazyGetTeacherList2Query,
  useGetAidedSchTchListQuery,
  useGetTeacherListForMpgQuery,
  // transfer
  usePostCounTransferMutation,
  usePostCounTransferLogMutation,
  useStaffCounsellingPostMutation,
  useTranferTeacherPostMutation,
  useTransferVacAddSubMutation,
  useTransferVacAddSub2Mutation,
  // vacancy selection
  useGetVacancyQuery,
  useLazyGetVacancyQuery,
  useGetSelectVacancyQuery,
  useLazyGetSelectVacancyQuery,
  // districts / blocks
  useGetAllDistrictListQuery,
  useLazyGetAllDistrictListQuery,
  useGetEduDistQuery,
  useGetAllEduDistQuery,
  useLazyGetAllEduDistQuery,
  // dashboard
  useLazyGetCounsellingStatusQuery,
  useLazyGetEduDistDashboardQuery,
  useGetAllBlockQuery,
  useLazyGetAllBlockQuery,
  useGetDisBlockQuery,
  useLazyGetDisBlockQuery,
  useGetEduDisBlockQuery,
  useLazyGetEduDisBlockQuery,
  useGetAllBlocksQuery,
  useLazyGetAllBlocksQuery,
  // priority
  useGetPriorityMasterQuery,
  useLazyGetPriorityMasterQuery,
  usePostNewPriorityMutation,
  usePostCnsnPriorityMutation,
  useGetCnsnPriorityQuery,
  useLazyGetCnsnPriorityQuery,
  // counselling update status
  useGetCounsellingUpdateStatusQuery,
  usePostCounsellingUpdateMutation,
  usePostAllCounsellingUpdateMutation,
  useGetLiveCounsellingStatusQuery,
  // counselling status
  useGetCounsellingStatusQuery,
  useGetCounsellingWiseStatusQuery,
  useGetOneCnsnSubSumryQuery,
  useGetInterDistrictQuery,
  // order copy / RC
  useGetTchrTypQuery,
  useGetRcNumQuery,
  useGetOfcrDetailsQuery,
  useUpdateOfcrDetailsMutation,
  useUpdateRcNumMutation,
  usePostOrderTemplateMutation,
  useGetOrderTemplateQuery,
  useLazyGetOrderTemplateQuery,
  useGetOrderCopyGridMasterQuery,
  useGetRcQuery,
  useLazyGetRcQuery,
  usePostSingleRcMutation,
  useGetPdfOrderCopyQuery,
  useLazyGetPdfOrderCopyQuery,
  // admin panel
  useGetCheckVacancyQuery,
  useLazyGetCheckVacancyQuery,
  useGetCheckCandidateQuery,
  // vacancy and need
  useGetVacancyNeedListQuery,
  useLazyGetVacancyNeedListQuery,
  usePostVacancyNeedMutation,
  useAddVacancyNeedMutation,
  useAddVacancyNeedLogMutation,
  useGetDesgVacNeedQuery,
  useLazyGetDesgVacNeedQuery,
  useGetTechVacQuery,
  useLazyGetTechVacQuery,
  useTechTransferVacPostMutation,
  useTechTransferVacPostAidedMutation,
  useGetVacncyUpdateStsQuery,
  useLazyGetVacncyUpdateStsQuery,
  usePostVacancyUpdateMutation,
  useGetBulkVacancyQuery,
  useLazyGetBulkVacancyQuery,
  useGetBulkSchFromUdiseQuery,
  useLazyGetBulkSchFromUdiseQuery,
  useGetResultantVacancySchoolsQuery,
  useUpdateResultantVacancyMutation,
  // school details
  useGetSchDetailsQuery,
  usePostChangeSchMutation,
  useGetTrackChangeDataQuery,
  useGetSchoolListQuery,
  // staff transfer / promotion
  useGetStaffTranQuery,
  usePostTeacherTransPromMutation,
  useUpdateStaffDetailsMutation,
  useGetTeacherTransAllQuery,
  // approvals
  useGetDropdownStaffQuery,
  useGetHmAprveSatusQuery,
  useGetHmAprveSatusTechIdQuery,
  useGetHmAprveSatusMutualQuery,
  useGetTchrPnlDistStsQuery,
  useLazyGetTchrPnlDistStsQuery,
  // seniority
  useGetOneSeniorityChallengeQuery,
  useGetSeniorityChallengeQuery,
  usePostVacancyChallengeMutation,
  useGetSeniorityOrderQuery,
  useLazyGetSeniorityOrderQuery,
  useBulkUploadSeniorityMutation,
  // challenge vacancies
  useGetChallengeVacQuery,
  usePostChallengeMutation,
  useGetChallengeDetailsQuery,
  // dashboard
  useGetEduDistDashboardQuery,
  useGetOverAllQuery,
  // aided school
  useVcntGoPostMutation,
  useGetVcntGoQuery,
  useLazyGetVcntGoQuery,
  useVcntApprovalPostMutation,
  // counselling mapping
  usePostCnsnMappingMutation,
  usePostTknCnsnRemoveCandidateMutation,
  // counselling start auth / SMS
  useCslnAuthMutation,
  useSmsSendingMutation,
  usePostSmsSendingOtpHistoryMutation,
} = counsellingApi;

/**
 * Angular exposed one route under several method names. Aliases keep those call
 * sites recognisable while sharing a single cache entry.
 */
export {
  // Angular: getState() / getBlock() / distlist()
  useGetAllDistrictListQuery as useGetStateQuery,
  useLazyGetAllDistrictListQuery as useLazyGetStateQuery,
  useGetAllDistrictListQuery as useDistListQuery,
  // Angular: postPromotion() / PostcherTransProm()
  usePostTeacherTransPromMutation as usePostPromotionMutation,
};
